import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hotel, Plane, Train, Bus, Car, Sparkles } from "lucide-react";
import ComingSoonModal from "@/components/ComingSoonModal";

/**
 * VerticalSelector — MakeMyTrip-style pill row of travel verticals shown on
 * the Landing hero. Hotels is live; the other five open a "Launching Soon"
 * modal that captures the user's email/phone into the waitlist.
 *
 * Design notes:
 *   - Dark placement (over the black hero) — uses white text + gold accents.
 *   - Active vertical (Hotels) has a soft-gold gradient chip; inactive tiles
 *     are muted glass with a "Soon" gold pill in the top-right corner.
 */
const VERTICALS = [
  { key: "hotels",   label: "Hotels",   icon: Hotel,    live: true  },
  { key: "flights",  label: "Flights",  icon: Plane,    live: false },
  { key: "trains",   label: "Trains",   icon: Train,    live: false },
  { key: "buses",    label: "Buses",    icon: Bus,      live: false },
  { key: "cabs",     label: "Cabs",     icon: Car,      live: false },
  { key: "holidays", label: "Holidays", icon: Sparkles, live: false },
];

export default function VerticalSelector({ variant = "dark", source = "landing" }) {
  const navigate = useNavigate();
  const [selectedSoon, setSelectedSoon] = useState(null);
  const isDark = variant === "dark";

  const onClick = (v) => {
    if (v.live) {
      // Hotels is the only active vertical today
      if (v.key === "hotels") navigate("/search");
    } else {
      setSelectedSoon(v.key);
    }
  };

  return (
    <div data-testid="vertical-selector" className="w-full">
      <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-none">
        {VERTICALS.map((v) => {
          const Icon = v.icon;
          const testId = `vertical-${v.key}`;
          const activeClass = v.live
            ? "ss-gold-gradient text-slate-900 border border-[#C8A34D] shadow-[0_10px_25px_-8px_rgba(200,163,77,0.55)]"
            : isDark
              ? "bg-white/[0.04] border border-white/10 text-white/70 hover:border-[#C8A34D]/60 hover:text-[#E8D8A8]"
              : "bg-white border border-slate-200 text-slate-700 hover:border-[#C8A34D] hover:text-[#A6852F]";

          return (
            <button
              key={v.key}
              type="button"
              data-testid={testId}
              onClick={() => onClick(v)}
              className={`relative flex flex-col items-center justify-center min-w-[86px] sm:min-w-[104px] px-4 py-3 rounded-2xl transition-all duration-200 flex-shrink-0 ${activeClass}`}
            >
              {!v.live && (
                <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-widest bg-[#C8A34D]/20 text-[#E8D8A8] rounded-full border border-[#C8A34D]/40">
                  Soon
                </span>
              )}
              <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${v.live ? "" : ""}`} strokeWidth={v.live ? 2.4 : 2} />
              <span className={`mt-1.5 text-xs sm:text-sm font-semibold tracking-wide ${v.live ? "text-slate-900" : ""}`}>{v.label}</span>
            </button>
          );
        })}
      </div>

      <ComingSoonModal
        vertical={selectedSoon}
        open={!!selectedSoon}
        onOpenChange={(o) => { if (!o) setSelectedSoon(null); }}
        source={source}
      />
    </div>
  );
}
